import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import { RootState } from "../store";
import notifySuccess from "../../utils/Notifications/Success";
import notifyError from "../../utils/Notifications/Error";

interface PendingCommand {
    tcp_queue_id: number;
    ticker_name: string;
    command_type: string;
    is_mass?: boolean;
}

export interface BackendLog {
    id: number;
    ticker_name: string;
    owner: string;
    run_date: string;
    status: "Pending" | "Error" | "Done";
    link: string | null;
    size: number | null;
}

export interface ILog {
    count: number;
    next: string;
    previous: string | null;
    results: BackendLog[];
}

export interface ILogToSave {
    ticker_name: string;
    start_date: string;
    end_date: string;
}

interface DownloadFileData {
    link: string;
    ticker_name: string;
}

type IState = {
    loading: boolean;
    error: null | string;
    logs: ILog;
    logsToSave: ILogToSave;
    isBlocked: boolean;
    logsStatus: boolean | string;
    downloadFileData?: DownloadFileData;
    uiBlock: "partial" | "full" | null;
    wsReadyStatuses: boolean;
    wsReadyLogs: boolean;
    pendingCommands: Record<number, PendingCommand>;
    massCommandPending: boolean;
    completedCommands: Record<number, true>;
};

const initialState: IState = {
    loading: false,
    error: null,
    logs: {} as ILog,
    isBlocked: true,
    logsToSave: {
        ticker_name: "",
        start_date: "",
        end_date: "",
    },
    logsStatus: "",
    uiBlock: null,
    wsReadyStatuses: false,
    wsReadyLogs: false,
    pendingCommands: {},
    massCommandPending: false,
    completedCommands: {},
};

export const getLogs = createAsyncThunk(
    "get/logs",
    async (page: number = 1, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `/api/logs?page=${page}`,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );

                return rejectWithValue(e.message);
            }
        }
    },
);

export const getLogsFile = createAsyncThunk(
    "get/logs/file",
    // @ts-ignore
    async (id: number, { rejectWithValue }) => {
        try {
            instance({
                url: pathname + `/api/logs/${id}`,
                responseType: "blob",
            }).then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data]),
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `file-${id}_color.xlsx`);
                document.body.appendChild(link);
                link.click();
            });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );

                return rejectWithValue(e.message);
            }
        }
    },
);

export const combineLogs = createAsyncThunk(
    "post/logs", // сведение логов
    async (_, { rejectWithValue, getState, dispatch }) => {
        const { logs } = getState() as RootState;
        const { logsToSave } = logs;
        try {
            const response = await instance.post(
                pathname + "/api/logs",
                logsToSave,
            );
            if (response.data.date_error) {
                notifyError(response.data.date_error);
            } else {
                notifySuccess(
                    `Запрос на сведение логов тикера: ${logsToSave.ticker_name} отправлен!`,
                );
                const { link, ticker_name } = response?.data;
                dispatch(
                    setDownloadFileData({
                        link,
                        ticker_name,
                    }),
                );
            }
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );

                return rejectWithValue(e.message);
            }
        }
    },
);
export const getLogsStatus = createAsyncThunk(
    "get/logsStatus",
    async (name: string, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `/api/save_logs/${name}`,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );

                return rejectWithValue(e.message);
            }
        }
    },
);

export const changeLogsStatus = createAsyncThunk(
    "change/logsStatus",
    async (_, { rejectWithValue, getState }) => {
        const { logs, settings } = getState() as RootState;
        const { logsStatus } = logs;
        const { activeTicker: ticker } = settings;
        const body = {
            flag: !logsStatus,
        };
        try {
            const response = await instance.patch(
                pathname + `/api/save_logs/${ticker.name}`,
                body,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );

                return rejectWithValue(e.message);
            }
        }
    },
);

const logsSlice = createSlice({
    name: "SettingsSlice",
    initialState,
    reducers: {
        setDownloadFileData(state, action) {
            const { ticker_name, link } = action.payload;

            state.downloadFileData = {
                ticker_name,
                link,
            };
        },
        resetState: () => initialState,
        setLogsTickerName: (state, action) => {
            state.logsToSave.ticker_name = action.payload;
        },
        setLogsStartDate: (state, action) => {
            state.logsToSave.start_date = action.payload;
        },
        setLogsEndDate: (state, action) => {
            state.logsToSave.end_date = action.payload;
        },
        setUiBlock: (state, action: { payload: "partial" | "full" | null }) => {
            state.uiBlock = action.payload;
        },
        setWsReadyStatuses: (state, action: { payload: boolean }) => {
            state.wsReadyStatuses = action.payload;
        },
        setWsReadyLogs: (state, action: { payload: boolean }) => {
            state.wsReadyLogs = action.payload;
        },
        setPendingCommand: (state, action: PayloadAction<PendingCommand>) => {
            state.pendingCommands[action.payload.tcp_queue_id] = action.payload;
        },
        removePendingCommand: (state, action: PayloadAction<number>) => {
            delete state.pendingCommands[action.payload];
        },
        setMassCommandPending: (state, action: PayloadAction<boolean>) => {
            state.massCommandPending = action.payload;
        },
        markCommandCompleted: (state, action: PayloadAction<number>) => {
            state.completedCommands[action.payload] = true;
        },

        removeCompletedCommand: (state, action: PayloadAction<number>) => {
            delete state.completedCommands[action.payload];
        },
    },
    extraReducers: {
        [getLogs.pending.type]: (state) => {
            state.loading = true;
        },
        [getLogs.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.paylaod;
        },
        [getLogs.fulfilled.type]: (state, action) => {
            state.loading = false;
            state.error = null;
            state.logs = action.payload;
        },
        [getLogsStatus.pending.type]: (state) => {
            state.loading = true;
        },
        [getLogsStatus.rejected.type]: (state, action) => {
            state.loading = false;
            state.isBlocked = true;
            state.error = action.paylaod;
        },
        [getLogsStatus.fulfilled.type]: (state, action) => {
            state.loading = false;
            state.isBlocked = false;

            state.error = null;
            state.logsStatus = action.payload;
        },
    },
});

export default logsSlice.reducer;
export const {
    resetState,
    setLogsTickerName,
    setLogsStartDate,
    setLogsEndDate,
    setDownloadFileData,
    setUiBlock,
    setWsReadyStatuses,
    setWsReadyLogs,
    removePendingCommand,
    setPendingCommand,
    setMassCommandPending,markCommandCompleted,removeCompletedCommand
} = logsSlice.actions;
